import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';

import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatOptionModule } from '@angular/material/core';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ClipboardModule } from '@angular/cdk/clipboard';

import { TaxDataService } from '../../services/tax-data-service/tax-data.service';
import { BruttoNettoCalculatorService, BruttoNettoState } from '../../services/brutto-netto-calculator-service/brutto-netto-calculator.service';
import { BUNDESLAENDER, KINDERFREIBETRAG_OPTIONS, STEUERKLASSEN, KRANKENVERSICHERUNGS_ARTEN, BruttoNettoYear } from '../../models/tax-params.model';

@Component({
  selector: 'metallgehalt-brutto-netto-calculator',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatIconModule,
    MatOptionModule,
    MatRadioModule,
    MatSnackBarModule,
    ClipboardModule
  ],
  templateUrl: './brutto-netto-calculator.component.html',
  styleUrls: ['./brutto-netto-calculator.component.scss']
})
export class BruttoNettoCalculatorComponent implements OnInit {
  private fb = inject(FormBuilder);
  private taxDataService = inject(TaxDataService);
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private route = inject(ActivatedRoute);
  private snackBar = inject(MatSnackBar);
  calcService = inject(BruttoNettoCalculatorService);

  private currentYear = new Date().getFullYear();

  form: FormGroup = this.fb.group({
    year: [this.currentYear, Validators.required],
    salaryMode: ['monthly'],
    brutto: [0, [Validators.required, Validators.min(0)]],
    steuerklasse: [3, Validators.required],
    kirchensteuer: [false],
    bundesland: ['nordrhein-westfalen'],
    kinderlos: [true],
    anzahlKinder: [0, [Validators.min(0), Validators.max(10)]],
    kinderfreibetraege: [0],
    kvZusatzbeitrag: [2.9, [Validators.required, Validators.min(0), Validators.max(5)]],
    gesetzlichRentenversichert: [true],
    gesetzlichArbeitslosenversichert: [true],
    krankenversicherungsArt: ['gesetzlich'],
    privatKvBeitrag: [0, [Validators.min(0)]],
    geldwerterVorteil: [0, [Validators.min(0)]],
    rememberInputs: [false]
  });

  private static readonly STORAGE_KEY = 'bruttoNettoParameters';

  availableYears = signal<BruttoNettoYear[]>([]);
  selectedYearInfo = signal<BruttoNettoYear | undefined>(undefined);

  readonly bundeslaender = BUNDESLAENDER;
  readonly steuerklassen = STEUERKLASSEN;
  readonly kinderfreibetragOptions = KINDERFREIBETRAG_OPTIONS;
  readonly krankenversicherungsArten = KRANKENVERSICHERUNGS_ARTEN;

  ngOnInit() {
    this.titleService.setTitle('Brutto-Netto-Rechner – Metallgehalt.de');
    this.metaService.updateTag({
      name: 'description',
      content: 'Brutto-Netto-Rechner: Berechne dein Nettogehalt aus dem Bruttogehalt. Mit Lohnsteuer, Sozialversicherung, Kirchensteuer und Solidaritätszuschlag.'
    });

    // Sync form values → service state
    this.form.valueChanges.subscribe(values => {
      const isMonthly = values.salaryMode === 'monthly';
      const brutto = values.brutto ?? 0;
      const monthlyBrutto = isMonthly ? brutto : this.round2(brutto / 12);

      const update: Partial<BruttoNettoState> = {
        bruttoMonthly: monthlyBrutto,
        bruttoYearlyInput: isMonthly ? null : brutto,
        steuerklasse: values.steuerklasse ?? 3,
        kirchensteuer: values.kirchensteuer ?? false,
        bundesland: values.bundesland ?? 'nordrhein-westfalen',
        kinderlos: values.kinderlos ?? true,
        anzahlKinder: values.anzahlKinder ?? 0,
        kinderfreibetraege: values.kinderfreibetraege ?? 0,
        kvZusatzbeitrag: (values.kvZusatzbeitrag ?? 2.9) / 100, // convert from % to decimal
        year: values.year ?? this.currentYear,
        gesetzlichRentenversichert: values.gesetzlichRentenversichert ?? true,
        gesetzlichArbeitslosenversichert: values.gesetzlichArbeitslosenversichert ?? true,
        krankenversicherungsArt: values.krankenversicherungsArt ?? 'gesetzlich',
        privatKvBeitrag: values.privatKvBeitrag ?? 0,
        geldwerterVorteil: values.geldwerterVorteil ?? 0
      };

      this.calcService.updateState(update);

      // Persist to localStorage if enabled
      if (values.rememberInputs) {
        localStorage.setItem(BruttoNettoCalculatorComponent.STORAGE_KEY, JSON.stringify(this.form.getRawValue()));
      }
    });

    // Load available years & restore saved inputs
    this.taxDataService.getBruttoNettoYears().subscribe(years => {
      this.availableYears.set(years);

      // Restore from localStorage if available
      const stored = JSON.parse(localStorage.getItem(BruttoNettoCalculatorComponent.STORAGE_KEY) ?? 'null');
      if (stored) {
        this.form.patchValue(stored);
        // Mark kvZusatzbeitrag as explicitly set so fetchTaxParams won't override it
        this.form.get('kvZusatzbeitrag')?.markAsDirty();
      } else {
        // Trigger initial state
        this.form.patchValue(this.form.getRawValue());
      }
    });

    // Toggle remember: save or clear localStorage
    this.form.get('rememberInputs')?.valueChanges.subscribe(remember => {
      if (remember) {
        localStorage.setItem(BruttoNettoCalculatorComponent.STORAGE_KEY, JSON.stringify(this.form.getRawValue()));
      } else {
        localStorage.removeItem(BruttoNettoCalculatorComponent.STORAGE_KEY);
      }
    });

    // Restore from URL query params (overrides localStorage)
    this.route.queryParams.subscribe(params => {
      if (params['bruttoMonthly']) {
        const parsed = this.parseQueryParams(params);
        this.form.patchValue(parsed);
        // Mark kvZusatzbeitrag as explicitly set so fetchTaxParams won't override it
        if (params['kvZusatzbeitrag']) {
          this.form.get('kvZusatzbeitrag')?.markAsDirty();
        }
      }
    });

    // When kinderlos changes, reset anzahlKinder
    this.form.get('kinderlos')?.valueChanges.subscribe(kinderlos => {
      if (kinderlos) {
        this.form.patchValue({ anzahlKinder: 0, kinderfreibetraege: 0 });
      }
    });
  }

  /** Load tax params whenever the year changes */
  fetchTaxParams = effect(() => {
    const year = this.calcService.state.year();
    if (year && this.availableYears().length) {
      const yearEntry = this.availableYears().find(y => y.year === year);
      this.selectedYearInfo.set(yearEntry);
      if (yearEntry) {
        this.taxDataService.getTaxParams(yearEntry.year).subscribe(params => {
          this.calcService.updateState({ taxParams: params });
          // Update KV default Zusatzbeitrag from params if available
          const defaultRate = params.socialSecurity.krankenversicherung.defaultAdditionalRate;
          if (defaultRate && this.form.get('kvZusatzbeitrag')?.pristine) {
            this.form.patchValue({ kvZusatzbeitrag: Math.round(defaultRate * 10000) / 100 }, { emitEvent: true });
          }
        });
      }
    }
  });

  get showKinderOptions(): boolean {
    return this.form.get('kinderlos')?.value === false;
  }

  get isPrivatKrankenversichert(): boolean {
    return this.form.get('krankenversicherungsArt')?.value === 'privat';
  }

  get showKvZusatzbeitrag(): boolean {
    return this.form.get('krankenversicherungsArt')?.value !== 'privat';
  }

  get isMonthlyMode(): boolean {
    return this.form.get('salaryMode')?.value === 'monthly';
  }

  copied(success: boolean) {
    if (success) {
      this.snackBar.open('Link in die Zwischenablage kopiert!', undefined, {
        duration: 2500,
        verticalPosition: 'top'
      });
    }
  }

  private parseQueryParams(params: any): any {
    return {
      year: params.year ? parseInt(params.year, 10) : this.currentYear,
      brutto: params.bruttoMonthly ? parseFloat(params.bruttoMonthly) : 0,
      steuerklasse: params.steuerklasse ? parseInt(params.steuerklasse, 10) : 1,
      kirchensteuer: params.kirchensteuer === 'true',
      bundesland: params.bundesland ?? 'nordrhein-westfalen',
      kinderlos: params.kinderlos !== 'false',
      anzahlKinder: params.anzahlKinder ? parseInt(params.anzahlKinder, 10) : 0,
      kinderfreibetraege: params.kinderfreibetraege ? parseFloat(params.kinderfreibetraege) : 0,
      kvZusatzbeitrag: params.kvZusatzbeitrag ? Math.round(parseFloat(params.kvZusatzbeitrag) * 10000) / 100 : 2.9,
      gesetzlichRentenversichert: params.gesetzlichRentenversichert !== 'false',
      gesetzlichArbeitslosenversichert: params.gesetzlichArbeitslosenversichert !== 'false',
      krankenversicherungsArt: params.krankenversicherungsArt ?? 'gesetzlich',
      privatKvBeitrag: params.privatKvBeitrag ? parseFloat(params.privatKvBeitrag) : 0,
      geldwerterVorteil: params.geldwerterVorteil ? parseFloat(params.geldwerterVorteil) : 0
    };
  }

  private round2(value: number): number {
    return Math.round(value * 100) / 100;
  }
}
